// Water composite pass — screen-space refraction, absorption, reflections
// Runs after opaque lighting. Reads copied opaque scene for refraction.
// Detects water pixels via negative albedo.a flag from ocean G-buffer.

import type { GPU } from "./gpu.js";
import type { GBuffer } from "./deferred.js";

const WATER_SHADER = /* wgsl */ `
struct WaterUniforms {
  invViewProj: mat4x4f,
  camPos: vec3f, _pad0: f32,
  lightDir: vec3f, giStrength: f32,
  voxelMin: vec3f, reflStrength: f32,
  voxelMax: vec3f, fogDensity: f32,
  fogColor: vec3f, screenW: f32,
  screenH: f32, refractionStr: f32, absorbMul: f32, sssMul: f32,
  absorbCoeff: vec3f, specPower: f32,
  deepColor: vec3f, sparkleMul: f32,
  sssColor: vec3f, ndlMul: f32,
};

@group(0) @binding(0) var albedoTex: texture_2d<f32>;      // G-buffer (now has ocean pixels)
@group(0) @binding(1) var normalTex: texture_2d<f32>;
@group(0) @binding(2) var depthTex: texture_depth_2d;       // depth buffer (ocean overwrote water pixels)
@group(0) @binding(3) var opaqueScene: texture_2d<f32>;     // lit opaque scene (pre-ocean)
@group(0) @binding(4) var sceneSampler: sampler;
@group(0) @binding(5) var voxelVolume: texture_3d<f32>;
@group(0) @binding(6) var voxelSampler: sampler;
@group(0) @binding(7) var<uniform> water: WaterUniforms;
@group(0) @binding(8) var giTex: texture_2d<f32>;
@group(0) @binding(9) var opaqueDepthTex: texture_depth_2d;  // pre-ocean depth copy (for water depth)

struct VSOut { @builtin(position) pos: vec4f, @location(0) uv: vec2f };

@vertex fn vsWater(@builtin(vertex_index) vi: u32) -> VSOut {
  let x = f32(i32(vi & 1u) * 4 - 1); let y = f32(i32(vi >> 1u) * 4 - 1);
  var o: VSOut; o.pos = vec4f(x, y, 0.0, 1.0); o.uv = vec2f((x + 1.0) * 0.5, (1.0 - y) * 0.5); return o;
}

fn reconstructWorldPos(coord: vec2i, depth: f32) -> vec3f {
  let screenSize = vec2f(water.screenW, water.screenH);
  let uv = (vec2f(coord) + 0.5) / screenSize;
  let ndc = vec4f(uv.x * 2.0 - 1.0, 1.0 - uv.y * 2.0, depth, 1.0);
  let world = water.invViewProj * ndc;
  return world.xyz / world.w;
}

fn worldToVoxelUV(wp: vec3f) -> vec3f {
  return (wp - water.voxelMin) / (water.voxelMax - water.voxelMin);
}

fn shadowHash(p: vec3f) -> f32 {
  return fract(sin(dot(p, vec3f(127.1, 311.7, 74.7))) * 43758.5453);
}

fn traceShadow(origin: vec3f, dir: vec3f, maxDist: f32) -> f32 {
  let voxelExtent = water.voxelMax - water.voxelMin;
  let voxelSize = max(voxelExtent.x, max(voxelExtent.y, voxelExtent.z)) / 256.0;
  var visibility = 1.0;
  let h = shadowHash(origin);
  var dist = voxelSize * 3.0 + h * voxelSize * 0.8;

  for (var i = 0; i < 16; i++) {
    if (visibility < 0.05 || dist > maxDist) { break; }
    let uv = worldToVoxelUV(origin + dir * dist);
    if (any(uv < vec3f(0.0)) || any(uv > vec3f(1.0))) { break; }
    let s = textureSampleLevel(voxelVolume, voxelSampler, uv, 0.0);
    // Translucent volume: transparent to shadow rays
    if (s.a < 0.9) { dist += voxelSize; continue; }
    visibility *= (1.0 - s.a);
    dist += voxelSize;
  }
  return visibility;
}

fn traceCone(origin: vec3f, dir: vec3f, coneAngle: f32) -> vec4f {
  var color = vec3f(0.0);
  var occlusion = 0.0;
  let voxelExtent = water.voxelMax - water.voxelMin;
  let voxelSize = max(voxelExtent.x, max(voxelExtent.y, voxelExtent.z)) / 256.0;
  let maxDist = length(voxelExtent) * 0.5;
  var dist = voxelSize * 4.0;
  let tanAngle = tan(coneAngle);

  for (var i = 0; i < 16; i++) {
    if (occlusion >= 0.95 || dist > maxDist) { break; }
    let samplePos = origin + dir * dist;
    let uv = worldToVoxelUV(samplePos);
    if (any(uv < vec3f(0.0)) || any(uv > vec3f(1.0))) { break; }
    let diameter = 2.0 * tanAngle * dist;
    let mipLevel = log2(max(1.0, diameter / voxelSize));
    let s = textureSampleLevel(voxelVolume, voxelSampler, uv, mipLevel);
    let stepLen = max(diameter * 0.5, voxelSize);

    if (s.a > 0.01 && s.a < 0.9) {
      let extinction = water.absorbCoeff * s.a * stepLen;
      color *= exp(-extinction);
    } else {
      let a = s.a * (1.0 - occlusion);
      color += s.rgb * a;
      occlusion += a;
    }
    dist += stepLen;
  }
  return vec4f(color, occlusion);
}

@fragment fn fsWater(in: VSOut) -> @location(0) vec4f {
  let coord = vec2i(in.pos.xy);
  let albedoSample = textureLoad(albedoTex, coord, 0);
  let emissive = albedoSample.a;

  if (emissive >= -0.05) {
    return textureLoad(opaqueScene, coord, 0);
  }

  let waveHeight = -emissive - 0.1;
  let depth = textureLoad(depthTex, coord, 0);
  let worldPos = reconstructWorldPos(coord, depth);
  let normalSample = textureLoad(normalTex, coord, 0);
  let N = normalize(normalSample.rgb);
  let roughness = normalSample.a;
  let V = normalize(water.camPos - worldPos);
  let linearDepth = length(worldPos - water.camPos);
  let waterAlbedo = albedoSample.rgb; // wave-height-based color from ocean vertex shader

  // --- Refraction ---
  let screenSize = vec2f(water.screenW, water.screenH);
  let distortion = N.xz * water.refractionStr / max(1.0, linearDepth * 0.08);
  let refractUV = clamp(in.uv + distortion, vec2f(0.002), vec2f(0.998));
  var refracted = textureSampleLevel(opaqueScene, sceneSampler, refractUV, 0.0).rgb;

  // Water depth (from pre-ocean depth copy)
  let behindCoord = vec2i(refractUV * screenSize);
  let behindDepth = textureLoad(opaqueDepthTex, behindCoord, 0);
  var waterDepth = 15.0;
  if (behindDepth < 0.99999) {
    let behindPos = reconstructWorldPos(behindCoord, behindDepth);
    waterDepth = max(0.0, worldPos.y - behindPos.y);
  }

  // Beer-Lambert absorption
  let transmittance = exp(-water.absorbCoeff * waterDepth * water.absorbMul);
  let deepColor = water.deepColor;
  refracted = refracted * transmittance + deepColor * (vec3f(1.0) - transmittance);

  // --- Direct surface lighting (this is what reveals wave shapes) ---
  var surfaceLight = vec3f(0.0);
  var shadow = 1.0;
  let sunIntensity = length(water.lightDir);
  var sunDir = vec3f(0.0, 1.0, 0.0);

  if (sunIntensity > 0.001) {
    sunDir = water.lightDir / sunIntensity;
    let ndl = max(dot(N, sunDir), 0.0);
    shadow = traceShadow(worldPos + N * 0.1, sunDir, 50.0);

    // Strong NdotL shading on the water surface color — reveals wave shapes
    surfaceLight = waterAlbedo * (ndl * water.ndlMul + 0.12) * sunIntensity * shadow;

    // Wave trough darkening: troughs are self-shadowed
    let troughDarken = clamp(1.0 - waveHeight * 1.8, 0.2, 1.0);
    surfaceLight *= troughDarken;

    // SSS: saturated emerald glow through thin wave crests
    let sssView = pow(max(dot(V, -sunDir), 0.0), 2.5);
    let sssWrap = max(0.0, 0.4 - ndl) * 2.5;
    let sssAmount = (sssView * 0.7 + sssWrap * 0.3) * waveHeight * sunIntensity * water.sssMul;
    surfaceLight += water.sssColor * sssAmount;
  } else {
    surfaceLight = waterAlbedo * 0.15;
  }

  // --- Reflection ---
  let R = reflect(-V, N);
  var traceOrigin = worldPos + N * 0.3;
  let reflResult = traceCone(traceOrigin, R, max(roughness * 0.8, 0.02));

  // Sky reflection varies with wave normal — tinted by ocean environment
  let skyUp = max(R.y, 0.0);
  let skyBase = water.fogColor * 1.0 + vec3f(0.01, 0.04, 0.04);
  let skyBright = skyBase + vec3f(0.02, 0.06, 0.06) * skyUp;
  let skyBlend = 1.0 - clamp(reflResult.a * 4.0, 0.0, 1.0);
  var reflColor = reflResult.rgb + skyBright * skyBlend;

  // --- Fresnel blend ---
  let cosTheta = max(dot(V, N), 0.0);
  let fresnel = 0.02 + 0.98 * pow(1.0 - cosTheta, 5.0);

  // Blend: refraction (see-through) vs reflection (mirror) vs surface color
  // At steep angles (looking down): mostly refraction + surface color
  // At grazing angles (looking across): mostly reflection
  let refractionPart = refracted * (1.0 - fresnel);
  let reflectionPart = reflColor * fresnel * water.reflStrength;
  // Surface lighting always contributes (this is what gives water its color and wave definition)
  var lit = refractionPart + reflectionPart + surfaceLight;

  // --- Sun specular (two layers) ---
  if (sunIntensity > 0.001) {
    let H = normalize(V + sunDir);
    let specFresnel = 0.04 + 0.96 * pow(1.0 - max(dot(V, H), 0.0), 5.0);

    // Broad specular — concentrated sun reflection on wave faces (controlled by specPower)
    let ndh = max(dot(N, H), 0.0);
    let broadSpec = pow(ndh, water.specPower) * sunIntensity * 0.8;
    lit += vec3f(1.0, 0.97, 0.92) * broadSpec * shadow;

    // Sparkles — very tight highlights that only fire when micro-normals
    // happen to align with the half-vector. Same normal, much higher power.
    let sparkleSpec = pow(ndh, 2000.0) * sunIntensity * water.sparkleMul;
    lit += vec3f(1.0, 0.98, 0.94) * sparkleSpec * shadow;
  }

  // --- Indirect from GI ---
  let giSample = textureSampleLevel(giTex, sceneSampler, in.uv, 0.0);
  lit += giSample.rgb * water.giStrength * 0.2;

  // Distance fog
  let dist = length(worldPos - water.camPos);
  let fogFactor = exp(-dist * water.fogDensity);
  lit = mix(water.fogColor, lit, clamp(fogFactor, 0.0, 1.0));

  return vec4f(lit, 1.0);
}
`;

export type OceanTweaks = {
  refractionStr: number;
  absorbMul: number;
  sssMul: number;
  absorbCoeff: [number, number, number];
  specPower: number;
  deepColor: [number, number, number];
  sparkleMul: number;
  sssColor: [number, number, number];
  ndlMul: number;
};

export const DEFAULT_OCEAN_TWEAKS: OceanTweaks = {
  refractionStr: 0.06,
  absorbMul: 4.3,
  sssMul: 4.7,
  absorbCoeff: [0.50, 0.03, 0.035],
  specPower: 250,
  deepColor: [0.0, 0.008, 0.02],
  sparkleMul: 20.0,
  sssColor: [0.016, 0.184, 0.090],
  ndlMul: 1.6,
};

export type WaterPassParams = {
  invViewProj: Float32Array;
  camPos: [number, number, number];
  lightDir: [number, number, number];
  giStrength: number;
  reflStrength: number;
  voxelMin: [number, number, number];
  voxelMax: [number, number, number];
  fogColor: [number, number, number];
  fogDensity: number;
  tweaks: OceanTweaks;
};

export class WaterComposite {
  private pipeline: GPURenderPipeline;
  private bindGroup: GPUBindGroup;
  private uniformBuffer: GPUBuffer;
  private uniformData = new Float32Array(52);

  constructor(
    private gpu: GPU,
    gBuffer: GBuffer,
    depthTexture: GPUTexture,
    opaqueScene: GPUTexture,
    opaqueDepthCopy: GPUTexture,
    voxelVolume: GPUTexture,
    voxelSampler: GPUSampler,
    giTexture: GPUTexture,
    targetFormat: GPUTextureFormat,
  ) {
    const { device } = gpu;

    const module = device.createShaderModule({ code: WATER_SHADER });

    const layout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },  // albedo
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },  // normal
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "depth" } },   // depth
        { binding: 3, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },  // opaqueScene
        { binding: 4, visibility: GPUShaderStage.FRAGMENT, sampler: {} },                        // sceneSampler
        { binding: 5, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float", viewDimension: "3d" } }, // voxel
        { binding: 6, visibility: GPUShaderStage.FRAGMENT, sampler: {} },                        // voxelSampler
        { binding: 7, visibility: GPUShaderStage.FRAGMENT, buffer: { type: "uniform" } },        // uniforms
        { binding: 8, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },  // giTex
        { binding: 9, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "depth" } },   // opaqueDepthCopy
      ],
    });

    this.pipeline = device.createRenderPipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [layout] }),
      vertex: { module, entryPoint: "vsWater" },
      fragment: { module, entryPoint: "fsWater", targets: [{ format: targetFormat }] },
      primitive: { topology: "triangle-list" },
    });

    // invViewProj(16) + 9 vec4s(36) = 52 floats = 208 bytes
    this.uniformBuffer = device.createBuffer({ size: 208, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });

    const sceneSampler = device.createSampler({ minFilter: "linear", magFilter: "linear" });

    this.bindGroup = device.createBindGroup({
      layout,
      entries: [
        { binding: 0, resource: gBuffer.albedo.createView() },
        { binding: 1, resource: gBuffer.normal.createView() },
        { binding: 2, resource: depthTexture.createView() },
        { binding: 3, resource: opaqueScene.createView() },
        { binding: 4, resource: sceneSampler },
        { binding: 5, resource: voxelVolume.createView({ dimension: "3d" }) },
        { binding: 6, resource: voxelSampler },
        { binding: 7, resource: { buffer: this.uniformBuffer } },
        { binding: 8, resource: giTexture.createView() },
        { binding: 9, resource: opaqueDepthCopy.createView() },
      ],
    });
  }

  render(encoder: GPUCommandEncoder, params: WaterPassParams, target: GPUTextureView): void {
    const { device, canvas } = this.gpu;
    const t = params.tweaks;
    const data = this.uniformData;
    // mat4: invViewProj (16 floats)
    data.set(params.invViewProj, 0);
    // vec4 4: camPos + pad
    data[16] = params.camPos[0]; data[17] = params.camPos[1]; data[18] = params.camPos[2];
    // vec4 5: lightDir + giStrength
    data[20] = params.lightDir[0]; data[21] = params.lightDir[1]; data[22] = params.lightDir[2]; data[23] = params.giStrength;
    // vec4 6: voxelMin + reflStrength
    data[24] = params.voxelMin[0]; data[25] = params.voxelMin[1]; data[26] = params.voxelMin[2]; data[27] = params.reflStrength;
    // vec4 7: voxelMax + fogDensity
    data[28] = params.voxelMax[0]; data[29] = params.voxelMax[1]; data[30] = params.voxelMax[2]; data[31] = params.fogDensity;
    // vec4 8: fogColor + screenW
    data[32] = params.fogColor[0]; data[33] = params.fogColor[1]; data[34] = params.fogColor[2]; data[35] = canvas.width;
    // vec4 9: screenH + refractionStr + absorbMul + sssMul
    data[36] = canvas.height; data[37] = t.refractionStr; data[38] = t.absorbMul; data[39] = t.sssMul;
    // vec4 10: absorbCoeff + specPower
    data[40] = t.absorbCoeff[0]; data[41] = t.absorbCoeff[1]; data[42] = t.absorbCoeff[2]; data[43] = t.specPower;
    // vec4 11: deepColor + sparkleMul
    data[44] = t.deepColor[0]; data[45] = t.deepColor[1]; data[46] = t.deepColor[2]; data[47] = t.sparkleMul;
    // vec4 12: sssColor + ndlMul
    data[48] = t.sssColor[0]; data[49] = t.sssColor[1]; data[50] = t.sssColor[2]; data[51] = t.ndlMul;

    device.queue.writeBuffer(this.uniformBuffer, 0, data);

    const pass = encoder.beginRenderPass({
      colorAttachments: [{
        view: target,
        loadOp: "clear", storeOp: "store",
        clearValue: { r: 0, g: 0, b: 0, a: 1 },
      }],
    });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    pass.draw(3);
    pass.end();
  }
}
